import cn from 'classnames';
import moment from 'moment';
import { useContext, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';

import Text from 'components/common/Text';
import { BasketContext, UserContext } from 'components/signUpFunnel/Contexts';
import { BrandContext } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { TEMPO } from 'lib/constants';
import { ALLOW_THANK_YOU_PAGE_DEVELOPMENT_WITHOUT_REDIRECTS } from 'lib/constants/featureFlags';
import styles from 'styles/components/signUpFunnel/thankYou/NextSteps';

import CutOffDateAndTime from './CutOffDateAndTime';
import { default as SmartphoneNextSteps } from './smartphone/NextSteps';
import ThankYouButton from './ThankYouButton';

const NextSteps = ({ isSmartphoneScreenSize = false, onClick }) => {
  const { basket, setBasket } = useContext(BasketContext);
  const { user } = useContext(UserContext);
  const { brand, postSignupUrl } = useContext(BrandContext);
  const isTempo = useBrand(TEMPO);
  const allowThankYouPageDevelopmentWithoutRedirects = useFeatureFlag(
    ALLOW_THANK_YOU_PAGE_DEVELOPMENT_WITHOUT_REDIRECTS
  );

  useEffect(() => {
    if (!allowThankYouPageDevelopmentWithoutRedirects) {
      return;
    }

    if (basket) {
      const existingBasket = localStorage.getItem('firstOrderBasket');
      if (!existingBasket) {
        localStorage.setItem('firstOrderBasket', JSON.stringify(basket));
      }
    } else {
      const storedBasket = localStorage.getItem('firstOrderBasket');
      if (storedBasket) {
        const parsedBasket = JSON.parse(storedBasket);

        setBasket(parsedBasket);
        localStorage.removeItem('firstOrderBasket');
      }
    }
  }, [basket, setBasket, allowThankYouPageDevelopmentWithoutRedirects]);

  const firstDeliveryDateFormatted = moment(basket?.deliveryDate).format('dddd, MMMM Do');

  const NextStepsContent = () => {
    return (
      <>
        <div
          className={cn('flex flex-col items-center text-center', {
            'bg-tempo-gray-100': isTempo,
            'bg-cream-110': !isTempo,
          })}
        >
          <Text brandStylesOverrides="h5 text-tempo-black-100">
            Your first box will be delivered on
          </Text>
          <Text brandStylesOverrides="font-medium text-primary h3" className="mb-4">
            {basket ? firstDeliveryDateFormatted : <Skeleton className={styles.skeleton150} />}
          </Text>
          <div className="flex items-center">
            <CutOffDateAndTime />
          </div>
          <Text brandStylesOverrides="textSm text-charcoal-90" className="mt-1 px-4">
            Heads up! You'll receive our pre-selected meals if you don't make changes before that
            date.
          </Text>
          <div className="mb-8 mt-4 w-full">
            <ThankYouButton
              onClick={onClick}
              postSignupUrl={postSignupUrl}
              user={user}
              variant="primary"
              className="w-full max-w-lg"
            />
          </div>
        </div>
      </>
    );
  };

  return isSmartphoneScreenSize ? (
    <SmartphoneNextSteps
      basket={basket}
      brand={brand}
      firstDeliveryDateFormatted={firstDeliveryDateFormatted}
      isSmartphoneScreenSize={isSmartphoneScreenSize}
      onClick={onClick}
      postSignupUrl={postSignupUrl}
      user={user}
    />
  ) : (
    <NextStepsContent />
  );
};

NextSteps.propTypes = {
  onClick: PropTypes.func.isRequired,
  isSmartphoneScreenSize: PropTypes.bool,
};

export default NextSteps;
